// DecisionIntelligenceGroup.jsx — v1.0.8 Document 4 Step 3
//
// Renders the four panels of the Decision Intelligence sidebar group:
//   1. System Status     — overall rollup with green / yellow / red dot
//   2. Active Issues     — counts of the four human-required alert types
//   3. Recent Decisions  — last 5 decision_log entries (placeholder until Orchestrator ships)
//   4. Decision Log Link — opens GET /api/admin/decision-log in a new tab
//
// Wires to:
//   GET /api/admin/decision-dashboard
//   GET /api/admin/decision-issues?status=open
//   GET /api/admin/decision-log?limit=5
//
// Auto-refreshes every 30s so the founder sees live data while triaging.
// Renders nothing fancy — matches the existing admin panel aesthetic exactly.
//
// The drawer (DecisionIssueDrawer.jsx, Step 5) and the full log table
// (DecisionLogTable.jsx, Step 6) are NOT included here; clicking an issue
// row currently fires `onIssueClick(id)` which the parent can wire to a
// drawer when it's built. Until then the parent passes a no-op or a toast.
import { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, FileText, ExternalLink, Brain, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 5-state alert dot colors (re-mapped to existing 8-color admin tokens).
const ALERT_DOT = {
  "AUTO-FIX FAILED": { bg: "bg-red-500", text: "text-red-600", pulse: true },
  "CODE FIX REVIEW REQUIRED": { bg: "bg-red-500", text: "text-red-600", pulse: false },
  "SPEC REVIEW REQUIRED": { bg: "bg-red-500", text: "text-red-600", pulse: false },
  "ARCHITECTURE REVIEW REQUIRED": { bg: "bg-red-500", text: "text-red-600", pulse: false },
  "AUTO-FIXED": { bg: "bg-emerald-400", text: "text-emerald-600", pulse: false },
};

const SYSTEM_DOT = {
  healthy: "bg-emerald-400",
  warning: "bg-amber-400",
  critical: "bg-red-500",
};

const POLL_MS = 30 * 1000;

// Order in which human-required alert states render in Panel 2 (Active Issues).
// Hoisted to module scope so static analyzers (incl. emergent visual-edits
// babel-metadata-plugin) can resolve the array source without traversing
// across the JSX boundary — fixes a TypeError observed in
// babel-metadata-plugin.js ImportDeclaration when iterating an inline literal.
const HUMAN_REQUIRED_STATES = [
  "AUTO-FIX FAILED",
  "CODE FIX REVIEW REQUIRED",
  "SPEC REVIEW REQUIRED",
  "ARCHITECTURE REVIEW REQUIRED",
];

export default function DecisionIntelligenceGroup({ onIssueClick }) {
  const [dashboard, setDashboard] = useState(null);
  const [issues, setIssues] = useState([]);
  const [recentDecisions, setRecentDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [d, i, l] = await Promise.all([
        axios.get(`${API}/admin/decision-dashboard`),
        axios.get(`${API}/admin/decision-issues?status=open&limit=20`),
        axios.get(`${API}/admin/decision-log?limit=5`),
      ]);
      setDashboard(d.data);
      setIssues(i.data?.items || []);
      setRecentDecisions(l.data?.items || []);
      setError(null);
      setLastFetched(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, POLL_MS);
    return () => clearInterval(id);
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-400" data-testid="di-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="ml-2 text-xs">Loading Decision Intelligence…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700"
        data-testid="di-error"
      >
        Decision Intelligence: {error}
      </div>
    );
  }

  const status = dashboard?.system_status || "healthy";
  const openByType = dashboard?.open_by_type || {};
  const deploymentBlocked = !!dashboard?.deployment_blocked;

  return (
    <div className="space-y-3" data-testid="decision-intelligence-group">
      {/* ─── Panel 1 — System Status ─────────────────────────────── */}
      <Panel
        testId="di-panel-system-status"
        icon={Activity}
        title="System Status"
        accent="bg-sky-100 text-sky-600"
      >
        <div className="flex items-center gap-3">
          <span
            className={`w-3 h-3 rounded-full ${SYSTEM_DOT[status]} ${
              status === "critical" ? "animate-pulse" : ""
            }`}
            data-testid="di-system-dot"
          />
          <span
            className="text-sm font-bold capitalize text-slate-800"
            data-testid="di-system-status-label"
          >
            {status}
          </span>
          {deploymentBlocked && (
            <span
              className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 uppercase tracking-wide"
              data-testid="di-deployment-blocked-badge"
            >
              Deployment Blocked
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Stat
            label="Active Issues"
            value={dashboard?.active_issues_count ?? 0}
            testId="di-stat-active"
          />
          <Stat
            label="Avg Confidence"
            value={(dashboard?.current_confidence_average ?? 0).toFixed(2)}
            testId="di-stat-confidence"
          />
          <Stat
            label="AutoFix Success 24h"
            value={`${Math.round((dashboard?.autofix_success_rate_last_24h ?? 0) * 100)}%`}
            testId="di-stat-autofix"
          />
          <Stat
            label="Last Decision"
            value={fmtTime(dashboard?.last_decision_timestamp)}
            testId="di-stat-last"
          />
        </div>
        {lastFetched && (
          <p className="text-[10px] text-slate-300 mt-3 text-right" data-testid="di-last-refreshed">
            Refreshed {lastFetched.toLocaleTimeString()}
          </p>
        )}
      </Panel>

      {/* ─── Panel 2 — Active Issues ─────────────────────────────── */}
      <Panel
        testId="di-panel-active-issues"
        icon={AlertTriangle}
        title="Active Issues"
        accent="bg-red-100 text-red-600"
      >
        {dashboard?.active_issues_count === 0 ? (
          <p className="text-xs text-slate-400 italic" data-testid="di-no-issues">
            No active issues. AutoFix has nothing to escalate right now.
          </p>
        ) : (
          <div className="space-y-1.5">
            {HUMAN_REQUIRED_STATES.map((state) => {
              const count = openByType[state] || 0;
              const matching = issues.filter((it) => it.alert_state === state);
              return (
                <IssueGroupRow
                  key={state}
                  state={state}
                  count={count}
                  items={matching}
                  onIssueClick={onIssueClick}
                />
              );
            })}
          </div>
        )}
      </Panel>

      {/* ─── Panel 3 — Recent Decisions ──────────────────────────── */}
      <Panel
        testId="di-panel-recent-decisions"
        icon={Brain}
        title="Recent Decisions"
        accent="bg-purple-100 text-purple-600"
      >
        {recentDecisions.length === 0 ? (
          <p className="text-xs text-slate-400 italic" data-testid="di-no-decisions">
            No decisions logged yet. The decision_log fills in once the Orchestrator at
            <code className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono text-[10px]">
              /library/orchestrator
            </code>{" "}
            ships and writes its first record.
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="di-recent-decisions-list">
            {recentDecisions.map((d) => (
              <li key={d.trace_id || d._id || JSON.stringify(d)}>
                <button
                  onClick={() => onIssueClick?.(d.trace_id || d.issue_id)}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 text-left transition"
                  data-testid={`di-recent-decision-${d.trace_id || ""}`}
                >
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      d.requires_human ? "bg-red-500" : d.simulation_mode ? "bg-amber-400" : "bg-emerald-400"
                    }`}
                  />
                  <span className="text-xs font-medium text-slate-700 flex-1 truncate">
                    {d.decision_type || "(unknown)"}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    {fmtConf(d.confidence)} · {d.mode_used || "—"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* ─── Panel 4 — Decision Log Link ─────────────────────────── */}
      <Panel
        testId="di-panel-log-link"
        icon={FileText}
        title="Decision Log"
        accent="bg-slate-100 text-slate-600"
      >
        <p className="text-xs text-slate-500 mb-3">
          Full filterable history of every decision the Orchestrator has ever made.
          Filterable by app, decision type, mode, confidence tier, human-required, and date.
        </p>
        <button
          onClick={openDecisionLog}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold transition shadow-sm"
          data-testid="di-open-decision-log"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          View Full Decision Log
        </button>
      </Panel>
    </div>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────

function Panel({ testId, icon: Icon, title, accent, children }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-4 h-4" />
        </span>
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2.5">
      <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">{label}</p>
      <p className="text-base font-bold text-slate-800 mt-0.5" data-testid={testId}>
        {value}
      </p>
    </div>
  );
}

function IssueGroupRow({ state, count, items, onIssueClick }) {
  const [open, setOpen] = useState(false);
  const dot = ALERT_DOT[state];
  if (count === 0) {
    // Don't render zero rows — keeps the panel compact.
    return null;
  }
  return (
    <div className="border border-slate-100 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition"
        data-testid={`di-issue-group-${state.replaceAll(" ", "-")}`}
      >
        <span
          className={`w-2 h-2 rounded-full flex-shrink-0 ${dot.bg} ${
            dot.pulse ? "animate-pulse" : ""
          }`}
        />
        <span className={`text-xs font-bold ${dot.text} flex-1 text-left`}>{state}</span>
        <span className="text-xs font-bold text-slate-700 px-2 py-0.5 rounded-full bg-slate-100">
          {count}
        </span>
      </button>
      {open && (
        <ul className="bg-slate-50 divide-y divide-slate-100" data-testid={`di-issue-list-${state.replaceAll(" ", "-")}`}>
          {items.map((it) => (
            <li key={it.issue_id}>
              <button
                onClick={() => onIssueClick?.(it.issue_id)}
                className="w-full text-left px-3 py-2 hover:bg-white transition"
                data-testid={`di-issue-row-${it.issue_id}`}
              >
                <p className="text-xs font-semibold text-slate-700 truncate">
                  {it.criterion_failed || "(no criterion)"}
                </p>
                <p className="text-[10px] text-slate-400 truncate">
                  {it.test_failed || "(no test description)"} · {fmtTime(it.created_at)}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function fmtConf(c) {
  if (c == null || isNaN(c)) return "—";
  return Number(c).toFixed(2);
}

function openDecisionLog() {
  // v1.0.8 Step 6 — open the proper filterable table page in a new tab.
  // The page handles its own auth via AdminRoute + axios bearer header.
  window.open("/admin/decision-log", "_blank", "noopener");
}

function escapeHtml(s) {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}
